import React, { useEffect, useState } from "react";
import { Building2, Heart, Search, Sparkles, Users, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "../../services/apiClient";
import { ACTIVATION_UI_COPY } from "../../constants/uiCopy";

const BasicDashboard = ({ user, navigate }) => {
  if (user?.role !== "basic") return null;

  const [summary, setSummary] = useState({
    favorites: 0,
    developments: 0,
    brokers: 0,
  });

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const [favoritesRes, developmentsRes, brokersRes] = await Promise.all([
          api.get("/favorites"),
          api.get("/developments", { auth: false }),
          api.get("/brokers", { auth: false }),
        ]);

        const [favoritesData, developmentsData, brokersData] = await Promise.all([
          favoritesRes.ok ? favoritesRes.json() : [],
          developmentsRes.ok ? developmentsRes.json() : [],
          brokersRes.ok ? brokersRes.json() : [],
        ]);

        setSummary({
          favorites: Array.isArray(favoritesData) ? favoritesData.length : 0,
          developments: Array.isArray(developmentsData)
            ? developmentsData.length
            : 0,
          brokers: Array.isArray(brokersData) ? brokersData.length : 0,
        });
      } catch (error) {
        console.error("Error fetching basic dashboard summary:", error);
      }
    };

    fetchSummary();
  }, []);

  const cards = [
    {
      title: "Explorar propiedades",
      description:
        "Descubre publicaciones activas y filtra por ciudad, tipo y presupuesto.",
      icon: Search,
      action: () => navigate("/search"),
      cta: "Ir al marketplace",
    },
    {
      title: "Ver desarrollos",
      description: "Conoce proyectos nuevos y compara unidades disponibles.",
      icon: Building2,
      action: () => navigate("/developments-list"),
      cta: "Explorar desarrollos",
    },
    {
      title: "Guardar favoritos",
      description:
        "Organiza propiedades y desarrollos que quieras revisar despues.",
      icon: Heart,
      action: () => navigate("/favorites"),
      cta: "Abrir favoritos",
    },
    {
      title: "Revisar mi cuenta",
      description: ACTIVATION_UI_COPY.basicAccountReviewDescription,
      icon: Wallet,
      action: () => navigate("/account"),
      cta: "Abrir mi cuenta",
    },
    {
      title: "Conectar con expertos",
      description:
        "Encuentra asesores y desarrolladoras para recibir atención directa.",
      icon: Users,
      action: () => navigate("/brokers-directory"),
      cta: "Ver brokers",
    },
  ];

  return (
    <div className="mb-6" data-testid="basic-dashboard">
      <div className="bg-gradient-to-br from-stone-950 via-stone-900 to-amber-900 text-white rounded-2xl p-6 md:p-8 mb-6">
        <p className="text-xs uppercase tracking-[0.24em] text-amber-200/80 mb-2">
          Resumen de cuenta básica
        </p>
        <h2
          className="text-2xl md:text-4xl font-bold mb-3"
          style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
        >
          Tu punto de partida para explorar con criterio
        </h2>
        <p className="max-w-2xl text-sm md:text-base text-stone-200">
          Usa tu cuenta gratuita para publicar <strong>1 propiedad sin costo</strong>, guardar favoritos, explorar desarrollos y avanzar hacia una operación más activa cuando estés listo.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button onClick={() => navigate("/search")}>
            Explorar propiedades
          </Button>
          <Button variant="secondary" onClick={() => navigate("/add-property")}>
            Publicar propiedad
          </Button>
          <Button variant="outline" onClick={() => navigate("/pricing")}>
            Ver planes y límites
          </Button>
          <Button variant="outline" onClick={() => navigate("/account")}>
            Mi Cuenta
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-background border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Favoritos</p>
          <p className="text-2xl font-bold">{summary.favorites}</p>
        </div>
        <div className="bg-background border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Desarrollos activos</p>
          <p className="text-2xl font-bold">{summary.developments}</p>
        </div>
        <div className="bg-background border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Asesores disponibles</p>
          <p className="text-2xl font-bold">{summary.brokers}</p>
        </div>
      </div>

      <div className="bg-background border border-border rounded-2xl p-5 md:p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Siguiente mejor paso</h3>
            <p className="text-sm text-muted-foreground">
              La cuenta Básico está pensada para descubrir, comparar y prepararte para operar cuando llegue el momento.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm">
            <p className="font-semibold mb-1">1. Explora</p>
            <p className="text-muted-foreground">
              Busca propiedades y desarrollos con contexto antes de decidir.
            </p>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm">
            <p className="font-semibold mb-1">2. Guarda y compara</p>
            <p className="text-muted-foreground">
              Usa favoritos para ordenar oportunidades y volver a revisarlas.
            </p>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm">
            <p className="font-semibold mb-1">3. Sube cuando operes</p>
            <p className="text-muted-foreground">
              {ACTIVATION_UI_COPY.basicUpgradeHint}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className="bg-background border border-border rounded-2xl p-5"
            >
              <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-sm text-muted-foreground mb-5">
                {card.description}
              </p>
              <Button variant="outline" onClick={card.action}>
                {card.cta}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { BasicDashboard };
